import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, BookOpen, Loader2, ArrowRight, MessagesSquare, Undo2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import InterviewUpgradeGate from "@/components/InterviewUpgradeGate";

export default function Interview() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const locked = user && !user.is_admin && (user.plan || "free") === "free";
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // {id,name,question_count}
  const [confirmText, setConfirmText] = useState("");
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashData, setTrashData] = useState(null);
  const [trashLoading, setTrashLoading] = useState(false);

  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get("/interview/trash");
      setTrashData(data);
    } catch (e) {
      toast.error(t("iv_trash_load_fail"));
    } finally {
      setTrashLoading(false);
    }
  };

  const restoreBank = async (id) => {
    try {
      await api.post(`/interview/banks/${id}/restore`);
      toast.success(t("iv_restored"));
      await loadTrash();
      await load();
    } catch (e) {
      toast.error(t("iv_restore_fail"));
    }
  };

  const restoreQuestion = async (id) => {
    try {
      await api.post(`/interview/questions/${id}/restore`);
      toast.success(t("iv_restored"));
      await loadTrash();
    } catch (e) {
      toast.error(t("iv_restore_fail"));
    }
  };

  const purgeBank = async (id) => {
    if (!window.confirm(t("iv_purge_confirm"))) return;
    try {
      await api.delete(`/interview/trash/banks/${id}`);
      await loadTrash();
    } catch (e) {
      toast.error(t("iv_delete_fail"));
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/interview/banks");
      setBanks(data || []);
    } catch (e) {
      toast.error(t("iv_load_fail"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!locked) load(); else setLoading(false); }, [locked]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/interview/banks", { name: newName.trim(), description: newDesc.trim() });
      setNewName(""); setNewDesc("");
      toast.success(t("iv_bank_created"));
      navigate(`/app/interview/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("iv_create_fail"));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (bank) => {
    setConfirmDelete(bank);
    setConfirmText("");
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    const bank = confirmDelete;
    try {
      const { data } = await api.delete(`/interview/banks/${bank.id}`);
      setBanks((b) => b.filter((x) => x.id !== bank.id));
      setConfirmDelete(null);
      // Show undo toast with restore action
      toast.success(t("iv_deleted_undo", { n: data.deleted_question_count || 0 }), {
        duration: 10000,
        action: {
          label: t("iv_undo"),
          onClick: async () => {
            try {
              await api.post(`/interview/banks/${bank.id}/restore`);
              await load();
              toast.success(t("iv_restored"));
            } catch (err) {
              toast.error(t("iv_restore_fail"));
            }
          },
        },
      });
    } catch (e) {
      toast.error(t("iv_delete_fail"));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="interview-library">
      {locked && <InterviewUpgradeGate />}
      {!locked && (<>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{t("iv_eyebrow")}</div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tighter">{t("iv_h1")}</h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t("iv_sub")}</p>
        </div>
        <button
          onClick={() => { setTrashOpen(true); loadTrash(); }}
          className="mt-2 inline-flex items-center gap-2 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 rounded-md font-semibold"
          data-testid="open-trash-btn"
        >
          <Trash2 className="w-4 h-4" /> {t("iv_trash")}
        </button>
      </div>

      <form onSubmit={handleCreate} className="mt-8 border border-zinc-200 bg-white p-6 rounded-md grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="interview-create-form">
        <input
          required
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t("iv_bank_name_ph")}
          className="md:col-span-1 px-4 py-3 border border-zinc-200 rounded-md focus:outline-none focus:border-blue-600"
          data-testid="interview-bank-name-input"
        />
        <input
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value)}
          placeholder={t("iv_bank_desc_ph")}
          className="md:col-span-1 px-4 py-3 border border-zinc-200 rounded-md focus:outline-none focus:border-blue-600"
          data-testid="interview-bank-desc-input"
        />
        <button
          type="submit"
          disabled={creating}
          className="btn-accent md:col-span-1 inline-flex items-center justify-center gap-2 font-semibold py-3 rounded-md disabled:opacity-60"
          data-testid="interview-create-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {t("iv_create")}
        </button>
      </form>

      <div className="mt-10">
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-bold">{t("iv_my_banks")}</h2>
          <span className="text-xs text-zinc-500">{banks.length} {t("iv_total")}</span>
        </div>
        {loading ? (
          <div className="mt-6 flex items-center gap-2 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /> {t("iv_loading")}</div>
        ) : banks.length === 0 ? (
          <div className="mt-6 border border-dashed border-zinc-300 bg-white p-10 rounded-md text-center">
            <BookOpen className="w-10 h-10 mx-auto text-zinc-400" />
            <div className="mt-3 font-semibold">{t("iv_empty_title")}</div>
            <div className="mt-1 text-sm text-zinc-500">{t("iv_empty_sub")}</div>
          </div>
        ) : (
          <ul className="mt-6 space-y-3">
            {banks.map((b) => (
              <li key={b.id} className="border border-zinc-200 bg-white p-5 rounded-md flex items-center justify-between gap-4 hover:border-zinc-400 transition-colors" data-testid={`interview-bank-${b.id}`}>
                <Link to={`/app/interview/${b.id}`} className="flex-1 min-w-0">
                  <div className="font-bold truncate">{b.name}</div>
                  <div className="text-sm text-zinc-500 truncate">{b.description || "—"}</div>
                  <div className="text-xs text-zinc-400 mt-1">{b.question_count} {t("iv_questions")}</div>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/app/interview/${b.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 hover:bg-zinc-50 rounded-md"
                    data-testid={`interview-bank-open-${b.id}`}
                  >
                    <ArrowRight className="w-4 h-4" /> {t("iv_open")}
                  </Link>
                  <button
                    onClick={() => handleDelete(b)}
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-zinc-300 text-red-600 hover:bg-red-50 rounded-md"
                    data-testid={`interview-bank-delete-${b.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-10 border border-zinc-200 bg-zinc-50 p-6 rounded-md flex items-start gap-4">
        <MessagesSquare className="w-6 h-6 text-zinc-700 shrink-0 mt-1" />
        <div>
          <div className="font-bold">{t("iv_hint_title")}</div>
          <p className="text-sm text-zinc-600 mt-1">{t("iv_hint_sub")}</p>
        </div>
      </div>

      {/* Trash modal */}
      {trashOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="trash-modal">
          <div className="bg-white rounded-md max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between gap-3 sticky top-0 bg-white pb-3 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                <h2 className="text-xl font-bold">{t("iv_trash_title")}</h2>
              </div>
              <button
                onClick={() => setTrashOpen(false)}
                className="text-zinc-500 hover:text-zinc-900 text-sm"
                data-testid="close-trash-btn"
              >✕</button>
            </div>
            <div className="mt-4">
              {trashLoading && (
                <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t("iv_loading")}</div>
              )}
              {trashData && trashData.banks.length === 0 && trashData.orphan_questions.length === 0 && (
                <div className="text-center text-zinc-500 py-10">
                  <BookOpen className="w-10 h-10 mx-auto text-zinc-300" />
                  <div className="mt-2">{t("iv_trash_empty")}</div>
                </div>
              )}
              {trashData && trashData.banks.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider">{t("iv_trash_banks")}</h3>
                  <ul className="mt-2 space-y-2">
                    {trashData.banks.map((b) => (
                      <li key={b.id} className="border border-zinc-200 bg-zinc-50 p-3 rounded-md flex items-center justify-between gap-3" data-testid={`trash-bank-${b.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold truncate">{b.name}</div>
                          <div className="text-xs text-zinc-500">{b.question_count || 0} {t("iv_questions")} · {new Date(b.deleted_at).toLocaleString()}</div>
                        </div>
                        <button
                          onClick={() => restoreBank(b.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold"
                          data-testid={`trash-restore-${b.id}`}
                        >
                          <Undo2 className="w-4 h-4" /> {t("iv_undo")}
                        </button>
                        <button
                          onClick={() => purgeBank(b.id)}
                          className="text-zinc-400 hover:text-red-600"
                          data-testid={`trash-purge-${b.id}`}
                          title={t("iv_purge")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {trashData && trashData.orphan_questions.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-zinc-700 uppercase tracking-wider">{t("iv_trash_questions")}</h3>
                  <ul className="mt-2 space-y-2">
                    {trashData.orphan_questions.map((q) => (
                      <li key={q.id} className="border border-zinc-200 bg-zinc-50 p-3 rounded-md flex items-center justify-between gap-3" data-testid={`trash-q-${q.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm">{q.question}</div>
                          <div className="text-xs text-zinc-500">{new Date(q.deleted_at).toLocaleString()}</div>
                        </div>
                        <button
                          onClick={() => restoreQuestion(q.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-md font-semibold"
                          data-testid={`trash-q-restore-${q.id}`}
                        >
                          <RotateCcw className="w-4 h-4" /> {t("iv_undo")}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-md max-w-md w-full p-6 border-2 border-red-500">
            <div className="text-red-600 font-bold uppercase tracking-wider text-xs">{t("iv_delete_warning")}</div>
            <div className="mt-2 text-xl font-black">{t("iv_delete_q", { n: confirmDelete.question_count || 0 })}</div>
            <p className="mt-2 text-sm text-zinc-600">
              {t("iv_delete_desc")}
              <span className="font-bold"> "{confirmDelete.name}"</span>
            </p>
            <p className="mt-3 text-xs text-zinc-500">{t("iv_delete_type_to_confirm", { name: confirmDelete.name })}</p>
            <input
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmDelete.name}
              className="mt-2 w-full px-3 py-2 border border-zinc-300 rounded-md focus:outline-none focus:border-red-500"
              data-testid="delete-confirm-input"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => { setConfirmDelete(null); setConfirmText(""); }}
                className="px-4 py-2 text-sm border border-zinc-300 rounded-md hover:bg-zinc-50"
                data-testid="delete-cancel-btn"
              >
                {t("iv_cancel")}
              </button>
              <button
                onClick={performDelete}
                disabled={confirmText.trim() !== confirmDelete.name}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed font-semibold"
                data-testid="delete-confirm-btn"
              >
                {t("iv_delete_btn")}
              </button>
            </div>
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}
